'use client';

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider } from 'next-themes';
import { Toaster, toast } from 'sonner';
import { useEffect } from 'react';
import { Building2 } from 'lucide-react';
import { useAuthStore } from '@/features/auth/stores/auth-store';
import { installModalBackdropGuard } from '@/shared/utils/modal-backdrop-guard';
import { showAfterReload } from '@/shared/utils/after-reload-toast';
import { ThemeApplier } from './theme-applier';
import { FontApplier } from './font-applier';
import { AppearancePrefsSync } from './appearance-prefs-sync';
import { NumberFormatProvider } from './number-format-provider';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      // Smart retry: NU reincerca pe 4xx (auth/permission/validation — erori
      // deterministe care vor esua oricat de mult le incerci). Reincerca o
      // singura data pe 5xx/network errors. Previne retry storm de 60+
      // requests cand un endpoint returneaza 403 in mod legitim.
      retry: (failureCount, error: any) => {
        const status = error?.statusCode ?? error?.status;
        if (typeof status === 'number' && status >= 400 && status < 500) return false;
        return failureCount < 1;
      },
    },
  },
});

function AuthInitializer({ children }: { children: React.ReactNode }) {
  const initialize = useAuthStore((s) => s.initialize);

  useEffect(() => {
    initialize();
    // Guard global: backdrop-ul modalelor inchide DOAR la click curat pe el,
    // nu cand un drag de selectie text din modal se termina in afara lui.
    installModalBackdropGuard();
  }, [initialize]);

  // Suprima AbortError global (provine din SDK-uri externe / fetch-uri abortate
  // la navigare rapida; nu indica bug real, doar zgomot in error overlay).
  useEffect(() => {
    const rejectionHandler = (e: PromiseRejectionEvent) => {
      const reason = e.reason as any;
      if (reason?.name === "AbortError" || (typeof reason?.message === "string" && reason.message.toLowerCase().includes("fetch is aborted"))) {
        e.preventDefault();
      }
    };
    const errorHandler = (e: ErrorEvent) => {
      const err = e.error as any;
      if (err?.name === "AbortError" || (typeof e.message === "string" && e.message.toLowerCase().includes("fetch is aborted"))) {
        e.preventDefault();
      }
    };
    window.addEventListener("unhandledrejection", rejectionHandler);
    window.addEventListener("error", errorHandler);
    return () => {
      window.removeEventListener("unhandledrejection", rejectionHandler);
      window.removeEventListener("error", errorHandler);
    };
  }, []);

  return <>{children}</>;
}

/**
 * Afiseaza un toast "Acum vezi {firma}" dupa ce switchTenant a facut hard
 * reload. switchTenant salveaza numele in sessionStorage inainte de reload;
 * showAfterReload il arata dupa montarea Toaster-ului (emis direct pe mount se
 * pierdea — mesajul nu aparea niciodata) si curata cheia la inchiderea lui.
 */
function TenantSwitchToast() {
  useEffect(() => {
    if (typeof window === 'undefined') return;
    return showAfterReload('tenantSwitchedTo', (name, clear) =>
      toast.success(`Acum vezi: ${name}`, {
        id: 'tenant-switched',
        icon: <Building2 className="h-4 w-4" />,
        duration: 4000,
        onAutoClose: clear,
        onDismiss: clear,
      }),
    );
  }, []);

  return null;
}

/**
 * După reîncărcarea cerută de o schimbare de sesiune (alt tab s-a deconectat sau a schimbat contul ori
 * firma, sau serverul a întors altă identitate): spunem de ce s-a reîncărcat pagina.
 */
function SessionChangedToast() {
  useEffect(() => {
    if (typeof window === 'undefined') return;
    return showAfterReload('sessionChangedElsewhere', (_, clear) =>
      toast.info('Sesiunea s-a schimbat (de exemplu, în alt tab), așa că pagina s-a reîncărcat.', {
        id: 'session-changed',
        duration: 6000,
        onAutoClose: clear,
        onDismiss: clear,
      }),
    );
  }, []);

  return null;
}

export function Providers({ children }: { children: React.ReactNode }) {
  return (
    <ThemeProvider attribute="class" defaultTheme="system" enableSystem={true}>
      <QueryClientProvider client={queryClient}>
        <AuthInitializer>
          <ThemeApplier />
          <FontApplier />
          <AppearancePrefsSync />
          <TenantSwitchToast />
          <SessionChangedToast />
          <NumberFormatProvider>{children}</NumberFormatProvider>
        </AuthInitializer>
      </QueryClientProvider>
      <Toaster
        position="top-right"
        richColors
        closeButton
        duration={4000}
        toastOptions={{
          className: 'font-sans',
        }}
      />
    </ThemeProvider>
  );
}
